import { WebAppInAppMessageViewDto } from "../iam/HackleWebAppInAppMessage"
import {
  DecisionReason,
  InAppMessageActionType,
  InAppMessageBehavior,
  Properties,
  User,
  VariationKey
} from "../../../core/internal/model/model"
import { InAppMessageEventType } from "../../iam/ui/event/InAppMessageViewEvent"
import { InAppMessageViewEventHandleType } from "../../iam/ui/event/InAppMessageViewEventHandleType"

export type InvocationCommand =
  | "getUser"
  | "getSessionId"
  | "setUser"
  | "setUserId"
  | "setDeviceId"
  | "setUserProperty"
  | "updateUserProperties"
  | "updatePushSubscriptions"
  | "updateSmsSubscriptions"
  | "updateKakaoSubscriptions"
  | "resetUser"
  | "variation"
  | "variationDetail"
  | "isFeatureOn"
  | "featureFlagDetail"
  | "track"
  | "showUserExplorer"
  | "hideUserExplorer"
  | "remoteConfig"
  | "setPhoneNumber"
  | "unsetPhoneNumber"
  | "isOptOutTracking"
  | "setOptOutTracking"
  | "getCurrentInAppMessageView"
  | "closeInAppMessageView"
  | "handleInAppMessageView"

export interface InvocationRequestDto {
  _hackle: {
    command: string
    parameters?: Record<string, any>
    browserProperties: Record<string, any>
    messageId?: string
  }
}

export interface InvocationResponseDto<T = any> {
  success: boolean
  message: string
  data?: T
}

export function invocationRequestDto<P extends Record<string, any> | never, R>(
  { command, parameters, browserProperties }: Invocation<P, R>,
  messageId?: string
): InvocationRequestDto {
  if (messageId === undefined) {
    return { _hackle: { command, parameters, browserProperties } }
  }
  return { _hackle: { command, parameters, browserProperties, messageId } }
}

export interface SetUserInvocationDto {
  user: User
}

export interface SetUserIdInvocationDto {
  userId: string | null
}

export interface SetDeviceIdInvocationDto {
  deviceId: string
}

export interface SetUserPropertyInvocationDto {
  key: string
  value: any
}

export interface UpdateUserPropertiesInvocationDto {
  operations: Record<string, any>
}

export interface UpdateSubscriptionsInvocationDto {
  operations: Record<string, string>
}

export interface VariationInvocationDto {
  experimentKey: number
  user?: User | string
}

export interface FeatureFlagInvocationDto {
  featureKey: number
  user?: User | string
}

export interface TrackInvocationDto {
  event: {
    key: string
    value?: number
    properties?: Properties<string>
  }
  user?: User | string
}

export interface RemoteConfigInvocationDto {
  key: string
  valueType: string
  defaultValue: string | number | boolean
  user?: User | string
}

export interface InvocationDecisionDto {
  variation: VariationKey
  reason: DecisionReason
  config: {
    parameters: Record<string, any>
  }
}

export interface InvocationFeatureFlagDecisionDto {
  isOn: boolean
  reason: DecisionReason
  config: {
    parameters: Record<string, any>
  }
}

export interface SetPhoneNumberInvocationDto {
  phoneNumber: string
}

export interface CloseInAppMessageViewInvocationDto {
  viewId: string
}

export interface InAppMessageEventDto {
  type: InAppMessageEventType
  action?: InAppMessageActionDto
  element?: InAppMessageElementDto
}

export interface InAppMessageActionDto {
  behavior: InAppMessageBehavior
  type: InAppMessageActionType
  value: string | null
}

interface InAppMessageElementDto {
  elementId?: string
  area?: string
}

export interface HandleInAppMessageViewInvocationDto {
  viewId: string
  handleTypes: InAppMessageViewEventHandleType[]
  event: InAppMessageEventDto
}

export interface SetOptOutTrackingInvocationDto {
  optOut: boolean
}

export interface Invocation<P extends Record<string, any> | never, R> {
  readonly command: InvocationCommand
  readonly parameters?: P
  readonly browserProperties: Properties<string>
  // R 추론용 팬텀 필드. 값을 채우지 않는다.
  readonly __result?: R
}

export interface SyncInvocation<P extends Record<string, any> | never, R> extends Invocation<P, R> {}

export interface AsyncInvocation<P extends Record<string, any> | never> extends Invocation<P, Promise<void>> {}

export interface GetUserInvocation extends SyncInvocation<never, User | null> {
  readonly command: "getUser"
}

export interface GetSessionIdInvocation extends SyncInvocation<never, string | null> {
  readonly command: "getSessionId"
}

export interface SetUserInvocation extends AsyncInvocation<SetUserInvocationDto> {
  readonly command: "setUser"
  readonly parameters: SetUserInvocationDto
}

export interface SetUserIdInvocation extends AsyncInvocation<SetUserIdInvocationDto> {
  readonly command: "setUserId"
  readonly parameters: SetUserIdInvocationDto
}

export interface SetDeviceIdInvocation extends AsyncInvocation<SetDeviceIdInvocationDto> {
  readonly command: "setDeviceId"
  readonly parameters: SetDeviceIdInvocationDto
}

export interface SetUserPropertyInvocation extends AsyncInvocation<SetUserPropertyInvocationDto> {
  readonly command: "setUserProperty"
  readonly parameters: SetUserPropertyInvocationDto
}

export interface UpdateUserPropertiesInvocation extends AsyncInvocation<UpdateUserPropertiesInvocationDto> {
  readonly command: "updateUserProperties"
  readonly parameters: UpdateUserPropertiesInvocationDto
}

export interface UpdatePushSubscriptionsInvocation extends AsyncInvocation<UpdateSubscriptionsInvocationDto> {
  readonly command: "updatePushSubscriptions"
  readonly parameters: UpdateSubscriptionsInvocationDto
}

export interface UpdateSmsSubscriptionsInvocation extends AsyncInvocation<UpdateSubscriptionsInvocationDto> {
  readonly command: "updateSmsSubscriptions"
  readonly parameters: UpdateSubscriptionsInvocationDto
}

export interface UpdateKakaoSubscriptionsInvocation extends AsyncInvocation<UpdateSubscriptionsInvocationDto> {
  readonly command: "updateKakaoSubscriptions"
  readonly parameters: UpdateSubscriptionsInvocationDto
}

export interface ResetUserInvocation extends AsyncInvocation<never> {
  readonly command: "resetUser"
}

export interface VariationInvocation extends SyncInvocation<VariationInvocationDto, string | null> {
  readonly command: "variation"
  readonly parameters: VariationInvocationDto
}

export interface VariationDetailInvocation
  extends SyncInvocation<VariationInvocationDto, InvocationDecisionDto | null> {
  readonly command: "variationDetail"
  readonly parameters: VariationInvocationDto
}

export interface IsFeatureOnInvocation extends SyncInvocation<FeatureFlagInvocationDto, boolean | null> {
  readonly command: "isFeatureOn"
  readonly parameters: FeatureFlagInvocationDto
}

export interface FeatureFlagDetailInvocation
  extends SyncInvocation<FeatureFlagInvocationDto, InvocationFeatureFlagDecisionDto | null> {
  readonly command: "featureFlagDetail"
  readonly parameters: FeatureFlagInvocationDto
}

export interface TrackInvocation extends AsyncInvocation<TrackInvocationDto> {
  readonly command: "track"
  readonly parameters: TrackInvocationDto
}

export interface ShowUserExplorerInvocation extends AsyncInvocation<never> {
  readonly command: "showUserExplorer"
}

export interface HideUserExplorerInvocation extends AsyncInvocation<never> {
  readonly command: "hideUserExplorer"
}

export interface RemoteConfigInvocation
  extends SyncInvocation<RemoteConfigInvocationDto, string | number | boolean | null> {
  readonly command: "remoteConfig"
}

export interface SetPhoneNumberInvocation extends AsyncInvocation<SetPhoneNumberInvocationDto> {
  readonly command: "setPhoneNumber"
  readonly parameters: SetPhoneNumberInvocationDto
}

export interface UnsetPhoneNumberInvocation extends AsyncInvocation<never> {
  readonly command: "unsetPhoneNumber"
}

export interface IsOptOutTrackingInvocation extends SyncInvocation<never, boolean | null> {
  readonly command: "isOptOutTracking"
}

export interface SetOptOutTrackingInvocation extends AsyncInvocation<SetOptOutTrackingInvocationDto> {
  readonly command: "setOptOutTracking"
  readonly parameters: SetOptOutTrackingInvocationDto
}

export interface GetCurrentInAppMessageViewInvocation extends SyncInvocation<never, WebAppInAppMessageViewDto | null> {
  readonly command: "getCurrentInAppMessageView"
}

export interface CloseInAppMessageViewInvocation extends AsyncInvocation<CloseInAppMessageViewInvocationDto> {
  readonly command: "closeInAppMessageView"
  readonly parameters: CloseInAppMessageViewInvocationDto
}

export interface HandleInAppMessageViewInvocation extends AsyncInvocation<HandleInAppMessageViewInvocationDto> {
  readonly command: "handleInAppMessageView"
  readonly parameters: HandleInAppMessageViewInvocationDto
}
